/**
 * Client-side error reporting.
 *
 * Two jobs:
 *  1. Always log to the console for local debugging.
 *  2. Fire-and-forget a beacon to POST /client-errors so a TERMINAL error the
 *     user hit (an ErrorBoundary crash, a failed-mutation toast, a network
 *     error they tapped "Report" on) surfaces in Cloud Logging — where the
 *     same alerting/triage that watches the platform can see it, instead of it
 *     dying invisibly in a browser console.
 *
 * The endpoint is auth-gated, so the beacon only fires when a token is present
 * (the common in-app case). A pre-auth crash still gets the local log. The
 * beacon NEVER throws and never surfaces a failure — reporting an error must
 * not itself produce one.
 *
 * Chat-answer errors do NOT come here: they reuse POST /messages/{id}/feedback
 * so they land in the admin Feedback explorer next to bad-reply flags.
 */

import { createLogger, recentLogEntries } from '@/utils/logger';
import { redactUuids } from '@/lib/redact';
import { useAuthStore } from '@/stores/auth';
import { getApiBaseUrl } from '@/api/baseUrl';

const logger = createLogger('ErrorReporter');

/**
 * The API call a report is ABOUT — the part that makes a report actionable.
 * Absent when the error was not a call (an ErrorBoundary render crash).
 */
export interface ApiRequestContext {
  method: string;
  /** UUID-masked API path, e.g. "/sessions" or "/sessions/:id/messages". */
  path: string;
  status: number;
  /**
   * The response's X-Request-ID — the join key to that request's server-side
   * log line. Absent when no response arrived (network failure, abort).
   */
  requestId?: string;
}

export interface ClientErrorReport {
  /** Short origin tag, e.g. "ErrorBoundary", "toast:createSession". */
  context: string;
  /** Sanitized, human-readable error text. */
  message: string;
  /** Structured code when known (e.g. an ApiError code, a turn-failure kind). */
  errorCode?: string;
  /** React component stack, for an ErrorBoundary crash. */
  componentStack?: string;
  /**
   * The failed call. Without it a report names the screen but not the endpoint
   * or the status, which is not enough to act on — the defect this field fixes.
   */
  request?: ApiRequestContext;
  /**
   * The conversation a failed turn happened in, and the seq of the message
   * that started it (`flo ops wait --seq` names the same key). A turn failure
   * arrives on the session event stream, where no ApiError exists to read
   * method/path/status from — these keys are what lets the server side of the
   * failure (its records, its prompt) be reached at all.
   */
  sessionId?: string;
  turnSeq?: number;
  /** The pinned agent the turn ran on, from the session cache. */
  agentSlug?: string;
  agentVersion?: string;
}

/**
 * The redacted browser pathname — the SCREEN the user was on. No query, no
 * hash, no ids. Distinct from the report's `request.path`, which is the call
 * that failed; a report wants both ("this broke on /home, calling /sessions").
 */
function redactedPath(): string {
  return redactUuids(window.location.pathname);
}

/**
 * Read the failed call off an error, for callers that hold one. Returns
 * undefined for anything not carrying a stamped path — real context or none,
 * never a placeholder that would read as a real endpoint.
 *
 * Structurally typed rather than `err instanceof ApiError` ON PURPOSE: importing
 * ApiError pulls in '@/api/client', which imports '@/i18n', whose module-load
 * side-effect runs i18n.init(). Any suite that mocks react-i18next without
 * initReactI18next then fails at IMPORT time — errorToast.tsx documents the same
 * trap and dodges it the same way. The reporter needs four fields off the error,
 * not its class.
 */
export function apiRequestContext(err: unknown): ApiRequestContext | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const e = err as {
    method?: unknown;
    path?: unknown;
    status?: unknown;
    requestId?: unknown;
  };
  if (typeof e.path !== 'string' || e.path === '' || typeof e.status !== 'number') {
    return undefined;
  }
  return {
    method: typeof e.method === 'string' ? e.method : 'GET',
    path: redactUuids(e.path),
    status: e.status,
    requestId: typeof e.requestId === 'string' ? e.requestId : undefined,
  };
}

/** POST the beacon. Fire-and-forget; swallows every failure. */
function sendBeacon(report: ClientErrorReport): void {
  try {
    const token = useAuthStore.getState().accessToken;
    if (!token) return; // endpoint is auth-gated; the local log still captured it
    void fetch(`${getApiBaseUrl()}/client-errors`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        context: report.context,
        message: report.message,
        error_code: report.errorCode,
        component_stack: report.componentStack,
        url: redactedPath(),
        request_method: report.request?.method,
        request_path: report.request?.path,
        request_status: report.request?.status,
        request_id: report.request?.requestId,
        session_id: report.sessionId,
        turn_seq: report.turnSeq,
        agent_slug: report.agentSlug,
        agent_version: report.agentVersion,
        // The ring rides EVERY beacon, not just turn reports: whatever the
        // browser logged before the failure is the context a report asks for.
        console_log: recentLogEntries(),
      }),
      keepalive: true, // survive an unload (e.g. a crash that navigates away)
    }).catch(() => {
      /* reporting must never surface a failure */
    });
  } catch {
    /* never throw from the reporter */
  }
}

/**
 * Report a terminal client error: logs locally AND beacons to the backend.
 * Used by the "Report" action on error surfaces (toasts, etc.).
 */
export function reportClientError(report: ClientErrorReport): void {
  logger.error('Client error reported', {
    context: report.context,
    message: report.message,
    errorCode: report.errorCode,
    request: report.request,
  });
  sendBeacon(report);
}

/** Bound the ErrorBoundary message now that reports are PERSISTED (not just
 *  logged): a pathological throw (an Error built from a large or sensitive
 *  payload) must not beacon an unbounded body. A render-crash message is a
 *  code-level diagnostic — usually a JS reason referencing field NAMES, not
 *  values — and the error type is the always-safe prefix; the componentStack
 *  carries the WHERE. This bounds blast radius; a clinical VALUE reaching here is
 *  a throw-site bug to fix where it's thrown, not something the reporter detects. */
const ERROR_BOUNDARY_MESSAGE_MAX = 500;

/**
 * Report a caught React error (ErrorBoundary). Kept as a thin wrapper over
 * reportClientError so the boundary's call site stays unchanged.
 */
export function reportError(error: Error, componentStack?: string): void {
  reportClientError({
    context: 'ErrorBoundary',
    message: `${error.name}: ${error.message}`.slice(0, ERROR_BOUNDARY_MESSAGE_MAX),
    componentStack,
  });
}

/** A JS stack is a code-level diagnostic like a component stack; bound it the same way a report bounds everything else. */
const STACK_MAX = 2000;
/** One report per distinct message in this window: a rejection storm is one defect, not a hundred beacons. */
const REPEAT_WINDOW_MS = 10_000;

/**
 * Report what nothing else catches: a promise rejection nobody awaited, and an
 * error thrown outside React's render. Neither reaches an ErrorBoundary or a
 * toast, so until now both died in the browser console — and under React 19 an
 * unhandled rejection can detonate the use()/cache dispatcher ("undefined is
 * not an object (evaluating 'e.useCache…')") and white-screen the page, with
 * no record of what rejected (20 October 2026, the care-plan board on usb;
 * api/endpoints/handoff.test.ts records the same class). Reporting does not
 * undo the crash; it names the promise to fix, with the console ring that
 * every beacon carries. The JS stack rides in component_stack: it is the one
 * stack field the report has, and a window-level error has no component.
 */
export function installGlobalErrorReporting(): void {
  if (typeof window === 'undefined') return;
  const recent = new Map<string, number>();
  const once = (message: string): boolean => {
    const now = Date.now();
    const last = recent.get(message);
    if (last !== undefined && now - last < REPEAT_WINDOW_MS) return false;
    recent.set(message, now);
    return true;
  };
  const describe = (reason: unknown): { message: string; stack?: string; code?: string } => {
    if (reason instanceof Error) {
      const code = (reason as { code?: unknown }).code;
      return {
        message: `${reason.name}: ${reason.message}`.slice(0, ERROR_BOUNDARY_MESSAGE_MAX),
        stack: reason.stack?.slice(0, STACK_MAX),
        code: typeof code === 'string' ? code : undefined,
      };
    }
    let text: string;
    try {
      text = typeof reason === 'string' ? reason : JSON.stringify(reason);
    } catch {
      text = String(reason);
    }
    return { message: `non-Error rejection: ${text ?? String(reason)}`.slice(0, ERROR_BOUNDARY_MESSAGE_MAX) };
  };

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const { message, stack, code } = describe(event.reason);
    if (!once(message)) return;
    reportClientError({
      context: 'unhandledrejection',
      message,
      errorCode: code,
      componentStack: stack,
      request: apiRequestContext(event.reason),
    });
  });

  window.addEventListener('error', (event: ErrorEvent) => {
    // A failed <img>/<script> load also arrives here, as an Event without an
    // error; it is not a JS fault and the ring would carry nothing about it.
    if (!(event.error instanceof Error)) return;
    const { message, stack, code } = describe(event.error);
    if (!once(message)) return;
    reportClientError({ context: 'window.error', message, errorCode: code, componentStack: stack });
  });
}
